"use client";

import { Divider } from "@/components/core/Divider";
import { Text } from "@/components/core/Text";
import { Button } from "@/components/core/buttons/Button";
import { IconButton } from "@/components/core/buttons/IconButton";
import { MixModal } from "@/components/core/modals/MixModal";
import { useMixContentQuery } from "@/graphql/generated";
import { useInsertMixContentViewMutation } from "@/graphql/generated";
import { useBookmark } from "@/hooks/useBookmark";
import { useContentComments } from "@/hooks/useContentComments";
import { useContentReaction } from "@/hooks/useContentReaction";
import { useRelatedContent } from "@/hooks/useRelatedContent";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { StreakToast } from "components/core/StreakToast";
import { useAuth } from "hooks/useAuth";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useReadingStreak } from "hooks/useReadingStreak";
import {
    BookmarkIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronLeftCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    HeartIcon,
    MessageCircleIcon,
    MoveUpRightIcon,
    Share2Icon,
    ShareIcon,
    XIcon
} from "lucide-react";
import { fireEvent } from "lib/ga";
import { useRouter } from "next/router";
import { ContentSection } from "types/app";
import { useCallback, useEffect, useRef, useState } from "react";

import { ContentCommentSection } from "./ContentCommentSection";
import { ContentTags } from "./cards/ContentCardLayout";
import { ContentCategoryChip } from "./cards/components/ContentCategoryChip";
import { ContentSourceAuthorHeader } from "./cards/components/ContentSourceAuthorHeader";
import { ContentThumbnailImg } from "./cards/components/ContentThumbnailImg";
import { ContentTitle } from "./cards/components/ContentTitle";

interface ContentDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    content: Mix_ContentFragment;
    onNavigate?: (direction: "prev" | "next") => void;
    canNavigatePrev?: boolean;
    canNavigateNext?: boolean;
    /**
     * 모달 표시 방식
     * - "modal": 고정 크기 모달 (기본값)
     * - "fullscreen": 전체 화면 오버레이
     */
    variant?: "modal" | "fullscreen";
    /**
     * 모달 오픈 방식
     * - "card_click": 콘텐츠 카드 클릭으로 열림
     * - "direct_link": 공유 링크로 직접 열림
     */
    openMethod?: "card_click" | "direct_link";
    /**
     * 콘텐츠가 위치한 섹션 (content_click 이벤트에 사용)
     * direct_link로 열렸을 경우 자동으로 "direct_link"로 설정됨
     */
    contentSection?: ContentSection;
}

const INITIAL_RELATED_ITEMS = 3;

export const RelatedContentsSection = ({
    content,
    className,
    onContentClick
}: {
    content: Mix_ContentFragment;
    className?: string;
    onContentClick?: (id: string) => void;
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const tags =
        content.mix_content_tags?.map((t) => t.mix_content_tag_enum.value) ||
        [];
    const { relatedContent, loading } = useRelatedContent({
        tags,
        limit: 10
    });

    const filteredContent = relatedContent.filter((c) => c.id !== content.id);
    const displayedContent = isExpanded
        ? filteredContent
        : filteredContent.slice(0, INITIAL_RELATED_ITEMS);
    const hasMoreItems = filteredContent.length > INITIAL_RELATED_ITEMS;

    if (!loading && filteredContent.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                "h-max rounded-xl bg-grey-100 p-3 dark:bg-[#242424]",
                className
            )}
        >
            <p
                className="text-[16px] font-bold text-grey-900
                    dark:text-grey-800"
            >
                관련 콘텐츠
            </p>
            <Divider className="my-3" />
            {loading ? (
                <Text variant="body4" tag="p" className="text-grey-500">
                    관련 콘텐츠를 불러오는 중...
                </Text>
            ) : (
                <>
                    <div className="flex flex-col gap-2">
                        {displayedContent.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => onContentClick?.(item.id)}
                                className="group flex w-full cursor-pointer
                                    flex-col gap-2 rounded-lg px-4 py-3
                                    text-left transition-colors
                                    hover:bg-[#e0e0e0] dark:hover:bg-[#303030]"
                            >
                                <div>
                                    <ContentTitle
                                        className="mb-1 line-clamp-2 text-sm
                                            leading-tight text-grey-900
                                            dark:hover:text-grey-800"
                                    >
                                        {item.title}
                                    </ContentTitle>
                                    <Text
                                        variant="caption1"
                                        tag="p"
                                        className="line-clamp-1 text-grey-600"
                                    >
                                        <span className="font-bold">
                                            {item.authorname}
                                        </span>{" "}
                                        •{" "}
                                        {dayjs(item.updatetime).format(
                                            "YYYY.MM.DD"
                                        )}
                                    </Text>
                                </div>
                            </button>
                        ))}
                    </div>
                    {hasMoreItems && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="mt-2 flex w-full items-center
                                justify-center gap-1 rounded-lg px-4 py-3
                                text-sm text-grey-600 transition-colors
                                hover:bg-[#e0e0e0] dark:hover:bg-[#303030]"
                        >
                            {isExpanded ? "접기" : "더보기"}
                            <ChevronDownIcon
                                className={cn(
                                    "size-4 transition-transform duration-200",
                                    isExpanded && "rotate-180"
                                )}
                            />
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

// 인기 토론 섹션 - 실제 댓글 수 기준으로 필터링 후 최대 3개 표시
// 후보를 6개 가져와 슬롯별 고정 훅으로 라이브 댓글 수를 조회, 0-0 항목을 제거함
const MAX_CANDIDATES = 6;
const MAX_DISPLAY = 3;

export const PopularDiscussionSection = ({
    currentContentId,
    className,
    onContentClick
}: {
    currentContentId: string;
    className?: string;
    onContentClick?: (id: string) => void;
}) => {
    const { data, loading } = useMixContentQuery({
        variables: {
            where: {
                id: { _neq: currentContentId },
                _or: [
                    { reaction_count: { _gt: 0 } },
                    { comment_count: { _gt: 0 } }
                ]
            },
            limit: MAX_CANDIDATES,
            orderBy: [
                { reaction_count: "desc_nulls_last" as any },
                { comment_count: "desc_nulls_last" as any }
            ]
        }
    });

    const candidates = data?.mix_content ?? [];

    // 슬롯별 고정 훅 호출 (React 훅 규칙 준수 — 루프 내 호출 불가)
    // skip으로 슬롯이 비어있을 때 쿼리 건너뜀, limit:1로 최소 데이터만 요청
    const slot0 = useContentComments({
        contentId: candidates[0]?.id ?? "",
        limit: 1,
        skip: !candidates[0]
    });
    const slot1 = useContentComments({
        contentId: candidates[1]?.id ?? "",
        limit: 1,
        skip: !candidates[1]
    });
    const slot2 = useContentComments({
        contentId: candidates[2]?.id ?? "",
        limit: 1,
        skip: !candidates[2]
    });
    const slot3 = useContentComments({
        contentId: candidates[3]?.id ?? "",
        limit: 1,
        skip: !candidates[3]
    });
    const slot4 = useContentComments({
        contentId: candidates[4]?.id ?? "",
        limit: 1,
        skip: !candidates[4]
    });
    const slot5 = useContentComments({
        contentId: candidates[5]?.id ?? "",
        limit: 1,
        skip: !candidates[5]
    });

    const slots = [slot0, slot1, slot2, slot3, slot4, slot5];

    // 로드 완료된 슬롯만 필터링 (로딩 중엔 보수적으로 유지)
    const validItems = candidates
        .map((item, i) => ({
            item,
            commentCount: slots[i]?.totalCount ?? 0,
            isSlotLoading: slots[i]?.isLoading ?? false
        }))
        .filter(
            ({ item, commentCount, isSlotLoading }) =>
                // 슬롯 로딩 중엔 항목 유지, 완료 후 반응·댓글 모두 0이면 제거
                isSlotLoading ||
                (item.reaction_count ?? 0) > 0 ||
                commentCount > 0
        )
        .slice(0, MAX_DISPLAY);

    if (!loading && validItems.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                "h-max rounded-xl bg-grey-100 p-3 dark:bg-[#242424]",
                className
            )}
        >
            <p
                className="text-[16px] font-bold text-grey-900
                    dark:text-grey-800"
            >
                인기 토론
            </p>
            <Divider className="my-3" />
            {loading ? (
                <Text variant="body4" tag="p" className="text-grey-500">
                    인기 토론을 불러오는 중...
                </Text>
            ) : (
                <div className="flex flex-col gap-2">
                    {validItems.map(({ item, commentCount }) => (
                        <button
                            key={item.id}
                            onClick={() => onContentClick?.(item.id)}
                            className="group flex w-full cursor-pointer flex-col
                                gap-2 rounded-lg px-4 py-3 text-left
                                transition-colors hover:bg-[#e0e0e0]
                                dark:hover:bg-[#303030]"
                        >
                            <div>
                                <ContentTitle
                                    className="mb-1 line-clamp-2 text-sm
                                        leading-tight text-grey-900
                                        dark:text-grey-800"
                                >
                                    {item.title}
                                </ContentTitle>
                                <div className="flex items-center gap-3">
                                    <Text
                                        variant="caption1"
                                        tag="span"
                                        className="flex items-center gap-1
                                            text-grey-600"
                                    >
                                        <HeartIcon className="size-3" />
                                        {item.reaction_count ?? 0}
                                    </Text>
                                    <Text
                                        variant="caption1"
                                        tag="span"
                                        className="flex items-center gap-1
                                            text-grey-600"
                                    >
                                        <MessageCircleIcon className="size-3" />
                                        {commentCount}
                                    </Text>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const ContentDetailModal = ({
    isOpen,
    onClose,
    content,
    onNavigate,
    canNavigatePrev = false,
    canNavigateNext = false,
    variant = "modal",
    openMethod = "direct_link",
    contentSection
}: ContentDetailModalProps) => {
    const router = useRouter();
    const isFullscreen = variant === "fullscreen";
    const [showFallback, setShowFallback] = useState(!content.imgurl);
    const { me, isLoggedIn, loading: authLoading, openSignInModal } = useAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const streakEnabled = isFeatureEnabled("DAILY_READING_STREAK");
    const [insertContentView] = useInsertMixContentViewMutation();

    // 스트릭 토스트 트리거 키 — 콘텐츠 오픈 후 스트릭을 다시 조회하여 토스트 표시
    const [streakTriggerKey, setStreakTriggerKey] = useState<string | null>(null);
    const { currentStreak, loading: streakLoading } = useReadingStreak(streakEnabled ? me?.id : undefined);

    // 모달이 열릴 때마다 조회수 1 증가 (게스트 포함, userid는 null 허용)
    useEffect(() => {
        if (!isOpen || authLoading) return;
        insertContentView({
            variables: {
                object: {
                    contentid: content.id,
                    userid: me?.id ?? null,
                    source: "web"
                }
            }
        })
            .then(() => {
                // view INSERT 후 1.5초 뒤 스트릭 재조회 → 토스트 트리거
                if (me?.id && streakEnabled) {
                    setTimeout(() => {
                        setStreakTriggerKey(`${content.id}-${Date.now()}`);
                    }, 1500);
                }
            })
            .catch((err) =>
                console.error("[ContentDetailModal] view 추적 실패:", err)
            );
        // content.id나 isOpen이 바뀔 때만 추적 (me 변경은 재추적 불필요)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, content.id]);

    // content_modal_open: direct_link로 열릴 때만 여기서 발생
    // card_click의 경우 카드 컴포넌트(ContentGrid 등)에서 이미 발생하므로 중복 방지
    // 익스텐션에서 넘어온 경우 _src=extension 쿼리로 origin platform 및 open_method 식별
    // 익스텐션 카드 클릭은 페이지 이동으로 인해 direct_link로 감지되지만 의미상 card_click
    useEffect(() => {
        if (!isOpen || openMethod !== "direct_link") return;
        const isFromExtension = router.query._src === "extension";
        const platform = isFromExtension ? "extension" : "web";
        const resolvedOpenMethod = isFromExtension ? "card_click" : "direct_link";
        fireEvent({
            event: "content_modal_open",
            params: {
                content_id: content.id,
                content_url: content.originalurl,
                content_title: content.title,
                // direct link 진입 시 UI 섹션 없음 — content_section 생략
                content_isoriginal: content.isoriginal,
                content_released: content.released,
                open_method: resolvedOpenMethod,
                platform
            }
        });
        // content.id나 isOpen이 바뀔 때만 발생
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, content.id]);

    const { toggle: toggleBookmark } = useBookmark(content);
    const isBookmarked = !!content.bookmarked;
    const {
        toggle: toggleReaction,
        isReacted,
        reactionCount
    } = useContentReaction(content);
    const commentSectionRef = useRef<HTMLDivElement>(null);
    const [isCopied, setIsCopied] = useState(false);
    const { totalCount: commentCount } = useContentComments({
        contentId: content.id
    });

    // 관련 콘텐츠 또는 인기 토론 클릭 시 해당 콘텐츠로 이동
    const handleContentClick = useCallback(
        (id: string) => {
            router.push(
                {
                    pathname: router.pathname,
                    query: router.query,
                    hash: `content?id=${id}`
                },
                undefined,
                { scroll: false }
            );
        },
        [router]
    );

    // 댓글 섹션으로 스크롤
    const scrollToComments = useCallback(() => {
        commentSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    }, []);

    // 키보드 이벤트 처리 (Escape만 처리, 좌우 화살표 네비게이션 비활성화)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    // 원문 보기 핸들러 — 모달 내 외부 링크 클릭 시 content_external_click 발생
    const handleOpenOriginal = () => {
        if (content.originalurl) {
            const platform =
                router.query._src === "extension" ? "extension" : "web";
            fireEvent({
                event: "content_external_click",
                params: {
                    content_id: content.id,
                    content_url: content.originalurl,
                    content_title: content.title,
                    content_section: contentSection,
                    content_isoriginal: content.isoriginal,
                    content_released: content.released,
                    platform
                }
            });
            window.open(content.originalurl, "_blank", "noopener,noreferrer");
        }
    };

    // 공유하기 핸들러 (클립보드에 URL 복사)
    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/contents/curated/${content.id}`;
        await navigator.clipboard.writeText(shareUrl);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    // YouTube 비디오 ID 추출 함수
    const extractYouTubeVideoId = (url: string): string | null => {
        const patterns = [
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
            /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/,
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }
        return null;
    };

    const videoId =
        content.source === "youtube" && content.originalurl
            ? extractYouTubeVideoId(content.originalurl)
            : null;

    return (
        <>
        <MixModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={cn(
                "overflow-y-auto backdrop-blur-sm",
                isFullscreen ? "items-stretch p-0" : "items-start p-4 md:p-10"
            )}
        >
            <div
                className={cn(
                    "relative flex w-full flex-col bg-white dark:bg-[#171717]",
                    isFullscreen
                        ? "min-h-screen"
                        : "mt-5 max-w-5xl rounded-2xl shadow-2xl"
                )}
            >
                {/* 네비게이션 바 */}
                <div
                    className="flex items-center justify-between
                        px-4 py-3"
                >
                    <div className="flex gap-2">
                        <div
                            onClick={() => onNavigate?.("prev")}
                            className={cn(
                                `cursor-pointer rounded-md p-2 transition-all
                                duration-300 hover:bg-[#e0e0e0]
                                dark:hover:bg-[#303030]`,
                                !canNavigatePrev &&
                                    "cursor-not-allowed opacity-30"
                            )}
                        >
                            <ChevronLeftIcon />
                        </div>
                        <div
                            onClick={() => onNavigate?.("next")}
                            className={cn(
                                `cursor-pointer rounded-md p-2 transition-all
                                duration-300 hover:bg-[#e0e0e0]
                                dark:hover:bg-[#303030]`,
                                !canNavigateNext &&
                                    "cursor-not-allowed opacity-30"
                            )}
                        >
                            <ChevronRightIcon />
                        </div>
                    </div>
                    <div
                        onClick={onClose}
                        className="cursor-pointer rounded-md transition-all
                            duration-300 hover:bg-[#e0e0e0]
                            dark:hover:bg-[#303030]"
                    >
                        <motion.button
                            onClick={onClose}
                            className="p-2"
                            whileHover={{ rotate: 90 }}
                            transition={{
                                duration: 0.2,
                                ease: [0.25, 0.46, 0.45, 0.94]
                            }}
                        >
                            <XIcon className="size-6 text-black dark:text-white" />
                        </motion.button>
                    </div>
                </div>

                {/* 모달 본문 */}
                <div
                    className={cn(
                        `flex flex-1 select-none gap-6 overflow-y-auto p-6
                        md:p-8`,
                        isFullscreen ? "mx-auto w-full max-w-5xl" : ""
                    )}
                >
                    {/* 메인 콘텐츠 */}
                    <div className="flex-1">
                        {/* 헤더 */}
                        <div className="mb-5">
                            <div className="mb-4 flex items-center gap-3">
                                <ContentSourceAuthorHeader
                                    isContentDetailModal
                                    sourceImgSize={40}
                                    sourceImgUrl={
                                        content.mix_source_info.imgurl
                                    }
                                    authorname={content.authorname}
                                    updatetime={content.updatetime}
                                    offlinetime={content.offlinetime}
                                    category={content.category}
                                    className="flex-1"
                                />
                                <div
                                    onClick={handleOpenOriginal}
                                    className="flex cursor-pointer items-center
                                        gap-1 rounded-full bg-black px-[16px]
                                        py-2 text-white transition-all
                                        duration-300 hover:bg-grey-600
                                        dark:bg-white dark:text-black
                                        dark:hover:bg-grey-300"
                                >
                                    원문 보기
                                    <MoveUpRightIcon className="size-4" />
                                </div>
                            </div>
                        </div>

                        {/* 제목 */}
                        <h1 className="mb-4 text-xl font-bold leading-tight">
                            {content.title}
                        </h1>

                        {/* 썸네일 */}
                        {videoId ? (
                            <div
                                className="relative mb-5 h-[382px]
                                    overflow-hidden rounded-xl"
                            >
                                <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    className="h-full w-full rounded-xl"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        ) : content.imgurl ? (
                            <a
                                href={content.originalurl ?? undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    // 썸네일 클릭으로 외부 페이지 이동 시 content_external_click 발생
                                    // YouTube embed는 iframe으로 처리되므로 이 경로는 실행되지 않음
                                    const platform =
                                        router.query._src === "extension"
                                            ? "extension"
                                            : "web";
                                    fireEvent({
                                        event: "content_external_click",
                                        params: {
                                            content_id: content.id,
                                            content_url: content.originalurl,
                                            content_title: content.title,
                                            content_section: contentSection,
                                            content_isoriginal:
                                                content.isoriginal,
                                            content_released: content.released,
                                            platform
                                        }
                                    });
                                }}
                            >
                                <div
                                    className="relative mb-5 h-[382px]
                                        overflow-hidden rounded-xl"
                                >
                                    <ContentThumbnailImg
                                        src={content.imgurl}
                                        fill
                                        unoptimized
                                        onFallbackChange={setShowFallback}
                                        className="rounded-xl object-cover"
                                    />
                                </div>
                            </a>
                        ) : null}

                        <div className="mb-6 flex items-center justify-between">
                            {/* 태그 */}
                            {content.mix_content_tags &&
                                content.mix_content_tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {content.mix_content_tags.map(
                                            (tagItem) => (
                                                <ContentTags
                                                    key={
                                                        tagItem
                                                            .mix_content_tag_enum
                                                            .value
                                                    }
                                                    tags={[tagItem]}
                                                />
                                            )
                                        )}
                                    </div>
                                )}
                            {content.category && (
                                <div>
                                    <ContentCategoryChip
                                        category={content.category}
                                    />
                                </div>
                            )}
                        </div>

                        {/* 설명 */}
                        {content.abstractcontents && (
                            <p className="mb-6 leading-relaxed">
                                {content.abstractcontents.length > 150
                                    ? `${content.abstractcontents.slice(0, 150)}...`
                                    : content.abstractcontents}
                            </p>
                        )}

                        {/* 액션 버튼 */}
                        <div
                            className="flex justify-center gap-4
                                py-5"
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={isLoggedIn ? toggleReaction : openSignInModal}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-[#e0e0e0] dark:hover:bg-[#303030]`,
                                    isReacted && "text-red-500"
                                )}
                            >
                                <HeartIcon
                                    className={cn(
                                        "size-5",
                                        isReacted &&
                                            "fill-current text-[#FF6B6B]"
                                    )}
                                />
                                <span>
                                    {/* 좋아요 */}
                                    {/* {reactionCount > 0 && ` ${reactionCount}`} */}
                                    {reactionCount}
                                </span>
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={isLoggedIn ? scrollToComments : openSignInModal}
                                className="flex items-center gap-2 rounded-md
                                    p-2 transition-all duration-300
                                    hover:bg-[#e0e0e0] dark:hover:bg-[#303030]"
                            >
                                <MessageCircleIcon className="size-5" />
                                <span>{commentCount}</span>
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={isLoggedIn ? toggleBookmark : openSignInModal}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-[#e0e0e0] dark:hover:bg-[#303030]`,
                                    isBookmarked &&
                                        "text-[var(--subcolor-orange)]"
                                )}
                            >
                                <BookmarkIcon
                                    className={cn(
                                        "size-5",
                                        isBookmarked &&
                                            "fill-current text-orange-500"
                                    )}
                                />
                                {/* <span>북마크</span> */}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={handleShare}
                                className={cn(
                                    `flex items-center gap-2 rounded-md p-2
                                    transition-all duration-300
                                    hover:bg-[#e0e0e0] dark:hover:bg-[#303030]`,
                                    isCopied && "text-green-500"
                                )}
                            >
                                {isCopied ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                            damping: 15
                                        }}
                                    >
                                        <CheckIcon className="size-5" />
                                    </motion.div>
                                ) : (
                                    <Share2Icon className="size-5" />
                                )}
                            </Button>
                        </div>

                        {/* 댓글 섹션 */}
                        <div ref={commentSectionRef} data-comment-section>
                            <ContentCommentSection
                                hideTopDivider
                                contentId={content.id}
                            />
                        </div>
                    </div>

                    {/* 사이드바 - 데스크톱에서만 표시 */}
                    <div
                        className="hidden w-64 flex-shrink-0 flex-col gap-4
                            lg:flex"
                    >
                        <RelatedContentsSection
                            content={content}
                            onContentClick={handleContentClick}
                        />
                        <PopularDiscussionSection
                            currentContentId={content.id}
                            onContentClick={handleContentClick}
                        />
                    </div>
                </div>
            </div>
        </MixModal>
        {streakEnabled && <StreakToast streak={currentStreak} triggerKey={streakTriggerKey} />}
        </>
    );
};
